import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import { DateTime } from "luxon";

import {
  OptionRow,
  getRecommendedSlot,
  AssignedResourceNote,
  DurationChips,
  groupDurationOptions,
  filterGroupByDuration,
  useGroupDuration
} from "/imports/ui/components/booking";
import { groupSlotsByLocation } from "../courtHelpers";

// Desktop multi-option picker: opens anchored to the clicked time chip so the
// user can pick a court/location for that slot.
function CourtOptionsPopover({ court }) {
  const { currency, filters, optionsAnchor, optionsGroup, optionsDate, closeOptionsPopover, handleSlotSelect, allowCourtPick } = court;

  // Several lengths at this time ("All durations"): a chip strip narrows the
  // rows to one length, and "Any Court" resolves within it.
  const [duration, setDuration] = useGroupDuration(optionsGroup);
  const durationOptions = groupDurationOptions(optionsGroup);
  const group = filterGroupByDuration(optionsGroup, duration);

  // "Any Court" books the court the picker chose — it ranks the free courts to
  // keep a coach's day together and avoid stranding time the club could sell.
  // Highlighted on top to nudge the client toward letting us optimize.
  const anySlot = getRecommendedSlot(group);

  // Court assigned by the club, but the time exists at several branches: the
  // branch is still the client's call, so offer it instead of the courts.
  const locationGroups = groupSlotsByLocation(group?.slots || []);
  const pickLocation = !allowCourtPick && locationGroups.length > 1;

  const choose = (slot) => {
    handleSlotSelect(slot, optionsDate);
    closeOptionsPopover();
  };

  return (
    <Popover
      open={!!optionsAnchor}
      anchorEl={optionsAnchor}
      onClose={closeOptionsPopover}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ sx: { p: 1.5, borderRadius: 2.5, width: "17.5rem", maxHeight: "23.75rem", boxShadow: "0 8px 30px rgba(26,26,26,0.16)" } }}
    >
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 1, px: 0.5 }}>
        {optionsGroup?.time}
        {optionsDate ? ` · ${DateTime.fromISO(optionsDate).toFormat("ccc MMM d")}` : ""}
      </Typography>
      <DurationChips options={durationOptions} value={duration} onChange={setDuration} currency={currency} />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, overflowY: "auto" }}>
        {!pickLocation && anySlot && (
          <OptionRow label="Any Court" sublabel="Recommended — best availability" selected onClick={() => choose(anySlot)} />
        )}

        {pickLocation &&
          locationGroups.map((group) => {
            const slot = getRecommendedSlot(group);
            return (
              <OptionRow
                key={group.locationId}
                label={group.locationName}
                sublabel={`${(slot.currency || currency || "").toUpperCase()} ${parseFloat(slot.price || 0).toFixed(2)}`}
                onClick={() => choose(slot)}
              />
            );
          })}

        {allowCourtPick &&
          (group?.slots || []).map((slot) => (
            <OptionRow
              key={`${slot.facilityId}-${slot.locationId}-${slot.pricingId}`}
              label={slot.facilityName}
              sublabel={[
                !filters.locationId && slot.locationName,
                `${(slot.currency || currency || "").toUpperCase()} ${parseFloat(slot.price || 0).toFixed(2)}`
              ]
                .filter(Boolean)
                .join(" · ")}
              onClick={() => choose(slot)}
            />
          ))}
      </Box>
      {!allowCourtPick && <AssignedResourceNote noun="Court" />}
    </Popover>
  );
}

export default CourtOptionsPopover;
